import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';

// Auth
import Login from './pages/Login';

// Student
import StudentLayout from './components/student/StudentLayout';
import StudentDashboard from './pages/student/StudentDashboard';
import UpcomingExams from './pages/student/UpcomingExams';
import ExamPage from './pages/student/ExamPage';
import StudentResults from './pages/student/StudentResults';
import StudentProfile from './pages/student/StudentProfile';

// Teacher
import TeacherLayout from './components/teacher/TeacherLayout';
import TeacherDashboard from './pages/teacher/TeacherDashboard';
import TeacherAssignments from './pages/teacher/TeacherAssignments';
import MyPapers from './pages/teacher/MyPapers';
import CreateExamPaper from './pages/teacher/CreateExamPaper';
import TeacherViewPaper from './pages/teacher/TeacherViewPaper';
import TeacherGradeSubmission from './pages/teacher/TeacherGradeSubmission';
import TeacherProfile from './pages/teacher/TeacherProfile';

// Admin
import AdminLayout from './components/admin/AdminLayout'; // Assuming there is one or creating one soon
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminTeacherAssignments from './pages/admin/AdminTeacherAssignments';
// import AdminSubjects from './pages/admin/AdminSubjects';
import AdminReviewPapers from './pages/admin/AdminReviewPapers';
import AdminExams from './pages/admin/AdminExams';
import AdminMonitor from './pages/admin/AdminMonitor';
import AdminResults from './pages/admin/AdminResults';

// Hall Manager
import HallManagerLayout from './components/hallManager/HallManagerLayout';
import HallManagerDashboard from './pages/hallManager/HallManagerDashboard';
import ManageHalls from './pages/hallManager/ManageHalls';
import ReviewPapersHallManager from './pages/hallManager/ReviewPapersHallManager';
import HallAllocations from './pages/hallManager/HallAllocations';
import ScheduleSupplyExams from './pages/hallManager/ScheduleSupplyExams';

// Exam Controller
import ExamControllerLayout from './components/examController/ExamControllerLayout';
import ExamControllerDashboard from './pages/examController/ExamControllerDashboard';
import ExamSessions from './pages/examController/ExamSessions';
import AssignHalls from './pages/examController/AssignHalls';
import ManageHallsEC from './pages/examController/ManageHalls';
import SeatPlan from './pages/examController/SeatPlan';
import ExamControllerReviewPapers from './pages/examController/ExamControllerReviewPapers';
import InvigilatorManage from './pages/examController/InvigilatorManage';

// Invigilator
import InvigilatorLayout from './components/invigilator/InvigilatorLayout';
import InvigilatorDashboard from './pages/invigilator/InvigilatorDashboard';
import UploadAnswers from './pages/invigilator/UploadAnswers';
import SeatSheet from './pages/invigilator/SeatSheet';

function App() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />

      {/* ── Student Routes ── */}
      <Route path="/student" element={
        <ProtectedRoute allowedRoles={['student']}>
          <StudentLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<StudentDashboard />} />
        <Route path="exams" element={<UpcomingExams />} />
        <Route path="exam/:examId" element={<ExamPage />} />
        <Route path="results" element={<StudentResults />} />
        <Route path="profile" element={<StudentProfile />} />
      </Route>

      {/* ── Teacher Routes ── */}
      <Route path="/teacher" element={
        <ProtectedRoute allowedRoles={['teacher']}>
          <TeacherLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<TeacherDashboard />} />
        <Route path="assignments" element={<TeacherAssignments />} />
        <Route path="papers" element={<MyPapers />} />
        <Route path="papers/:id" element={<TeacherViewPaper />} />
        <Route path="papers/:id/edit" element={<CreateExamPaper editMode={true} />} />
        <Route path="submissions/:submissionId" element={<TeacherGradeSubmission />} />
        <Route path="create-paper" element={<CreateExamPaper />} />
        <Route path="profile" element={<TeacherProfile />} />
      </Route>

      {/* ── Admin Routes ── */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
          {/* If AdminLayout isn't defined yet, this might error, but assuming it was imported or will be soon */}
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="teacher-assignments" element={<AdminTeacherAssignments />} />
        {/* <Route path="subjects" element={<AdminSubjects />} /> */}
        <Route path="review-papers" element={<AdminReviewPapers />} />
        <Route path="exams" element={<AdminExams />} />
        <Route path="monitor" element={<AdminMonitor />} />
        <Route path="results" element={<AdminResults />} />
      </Route>

      {/* ── Hall Manager Routes ── */}
      <Route path="/hall-manager" element={
        <ProtectedRoute allowedRoles={['hall_manager']}>
          <HallManagerLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<HallManagerDashboard />} />
        <Route path="halls" element={<ManageHalls />} />
        <Route path="allocations" element={<HallAllocations />} />
        <Route path="supply-exams" element={<ScheduleSupplyExams />} />
        <Route path="review-papers" element={<ReviewPapersHallManager />} />
        {/*
        <Route path="profile" element={<HallManagerProfile />} />
        */}
      </Route>

      {/* ── Exam Controller Routes ── */}
      <Route path="/exam-controller" element={
        <ProtectedRoute allowedRoles={['exam_controller']}>
          <ExamControllerLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<ExamControllerDashboard />} />
        <Route path="sessions" element={<ExamSessions />} />
        <Route path="assign-halls" element={<AssignHalls />} />
        <Route path="manage-halls" element={<ManageHallsEC />} />
        <Route path="seat-plan" element={<SeatPlan />} />
        <Route path="review-papers" element={<ExamControllerReviewPapers />} />
        <Route path="invigilators" element={<InvigilatorManage />} />
      </Route>

      {/* ── Invigilator Routes ── */}
      <Route path="/invigilator" element={
        <ProtectedRoute allowedRoles={['invigilator']}>
          <InvigilatorLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<InvigilatorDashboard />} />
        <Route path="seat-sheet" element={<SeatSheet />} />
        <Route path="upload-answers" element={<UploadAnswers />} />
      </Route>

      {/* Catch-all → login */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default App;
